import type {GroupEntry, GroupRepo, RecipeEntry, RecipeId} from "../../models/GroupRepo";
import PlusMinusButtons from "../../shared/PlusMinusButtons";
import {Col, Row, Text} from "../../ui/Box";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import {MiniPill} from "../items/ItemPill";
import type {Potlist} from "./types";
import {removeFromPotlist, setPotlistPortionCount, usePotlist} from "./usePotItems";

type PotlistEntry = [RecipeId, Potlist[RecipeId]];

const Tile = ({
  recipeId,
  recipe,
  portionCount,
  group,
}: {
  recipeId: RecipeId;
  recipe: RecipeEntry | null;
  portionCount: number;
  group: GroupEntry;
}) => {
  const handleUpdate = (next: number) => {
    setPotlistPortionCount(group.id, recipeId, next);
  };
  const handleDelete = () => {
    removeFromPotlist(group.id, recipeId);
  };

  return (
    <Row
      surface
      borderWidth={1}
      borderColor="default"
      sp="12"
      width="100%"
      maxWidth="formWidth"
      align="center"
      rounded="4"
    >
      <Text size="16" color="primary" px="8" py="4">
        {recipe ? recipe.name : "Deleted Recipe"}
      </Text>
      <Row ml="auto" align="center" sp="8">
        <MiniPill>{portionCount}</MiniPill>
        {recipe ? (
          <PlusMinusButtons
            value={portionCount}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            scale="one"
          />
        ) : (
          <Button variant="primary" size="md" onClick={handleDelete}>
            <Icon name="trash" />
          </Button>
        )}
      </Row>
    </Row>
  );
};

const PotlistView = ({
  entries,
  group,
  groupRepo,
}: {
  entries: PotlistEntry[];
  group: GroupEntry;
  groupRepo: GroupRepo;
}) => {
  const allRecipes = groupRepo.Recipes.useGetAll();
  return (
    <Col sp="12">
      <Col sp="4">
        {entries.map(([recipeId, {portionCount}]) => (
          <Tile
            key={recipeId}
            recipeId={recipeId}
            recipe={allRecipes[recipeId] ?? null}
            portionCount={portionCount}
            group={group}
          />
        ))}
      </Col>
      <Col align="center">
        <Button variant="primary" to="../schedule">
          Prepare Ingredients
        </Button>
      </Col>
    </Col>
  );
};

const PotlistDrawer = ({groupRepo}: {groupRepo: GroupRepo}) => {
  const group = groupRepo.vals.useValues();
  const potlist = usePotlist(group.id) ?? {};
  const entries = Object.entries(potlist) as PotlistEntry[];

  return (
    <Col sp="24" maxWidth="formWidth" width="100%" mr="auto" ml="auto" pb="24">
      <h1>Prepare List</h1>
      {entries.length === 0 ? (
        <Text>
          List is empty. Add recipes via <Icon name="pot-plus" inline />
        </Text>
      ) : (
        <PotlistView entries={entries} group={group} groupRepo={groupRepo} />
      )}
    </Col>
  );
};

export default PotlistDrawer;
